import { MaintenanceWindowDetails } from '@totem/components/maintenanceWindows/types';
import {
  ForeignKey,
  ObjectReference,
  PagingResults,
} from '@totem/types/common';
export const DEVICE_COLOR_CRITICAL = '#D90000';
export const DEVICE_COLOR_MAJOR = '#F07800';
export const DEVICE_COLOR_MINOR = '#F0A400';
export const DEVICE_COLOR_WARNING = '#F0CB00';
export const DEVICE_COLOR_INFORMATION = '#378FEF';
export const DEVICE_COLOR_OK = '#B5E61D';
export const DEVICE_COLOR_UNKNOWN = '#606060';

export const DEVICE_TEXT_COLOR_CRITICAL = '#FFFFFF';
export const DEVICE_TEXT_COLOR_MAJOR = '#FFFFFF';
export const DEVICE_TEXT_COLOR_MINOR = '#FFFFFF';
export const DEVICE_TEXT_COLOR_WARNING = '#FFFFFF';
export const DEVICE_TEXT_COLOR_INFORMATION = '#FFFFFF';
export const DEVICE_TEXT_COLOR_OK = '#FFFFFF';
export const DEVICE_TEXT_COLOR_UNKNOWN = '#FFFFFF';

export interface DeviceAction {
  name: string;
  action: (action: string, device: Device) => void;
  replaceDefault: boolean;
}

export interface DeviceQueryResults {
  devices: DeviceQueryResult[];
  paging: PagingResults;
}

export interface OperatingSystemEndOfSupport {
  isEndOfSupport: boolean;
  approachingEndOfSupport: boolean;
  endOfSupportDate: string;
}

export interface DeviceQueryResult {
  device: Device;
  organization: ObjectReference;
  region: ObjectReference;
  building: ObjectReference;
  controlSystem: ObjectReference;
  operatingSystemEndOfSupport?: OperatingSystemEndOfSupport;
  volumesWithLowFreeSpace: number;
}

export interface DevicePaging {
  page: number;
  pageSize: number;
  totalRecords: number;
}

export interface DeviceContractLine {
  contractId: string;
  billingContainerId: string;
  lineId: string;
  orderNumber: string;
  lineNumber: number;
  service: string;
  status: string;
}

export interface DeviceSalesOrder {
  salesOrderId: string;
  salesOrderNumber: string;
  lineNumber: number;
  service: string;
  status: string;
}

export interface SourcedAttribute {
  source: string;
  name: string;
  value: string;
  dataType: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  lastSeenAt: string;
  active: boolean;
}

export interface Device {
  id: string;
  organizationId: string;
  regionId: string;
  buildingId: string;
  controlSystemId: string;
  displayName: string;
  hostname?: string;
  type: string;
  vendor: string;
  operatingSystem: string;
  firmware: string;
  keys: DeviceKey[];
  ipAddresses: string[];
  organization: DeviceOrganization;
  region: DeviceRegion;
  building: DeviceBuilding;
  controlSystem: DeviceControlSystem;
  createdAt: string;
  createdBy: string;
  lastBackup?: DeviceLastBackup;
  contractLines?: DeviceContractLine[];
  salesOrders?: DeviceSalesOrder[];
  isManaged: boolean;
  attributes: DeviceAttribute[];
  software: Software[];
  peripherals: Peripheral[];
  drives: Drive[];
  services: DeviceService[];
  memorySlots: MemorySlot[];
  videoCards: VideoCard[];
  flags: DeviceFlags;
  variableCollection: VariableCollection[];
  maintenanceWindows: MaintenanceWindowDetails[];
  endOfLife: DeviceEndOfLife[];
  sourcedAttributes: SourcedAttribute[];
}

export interface DeviceEndOfLife {
  source: string;
  date: string; // ISO datetime string
  productType: string;
  id: string;
  name: string;
  endOfSupportDate: string;
  isPastEndOfSupport: boolean;
  endOfLifeDate: string;
  isPastEndOfLife: boolean;
  discontinuedDate: string;
  isDiscontinued: boolean;
}

export interface DeviceFlags {
  managed: DeviceFlag;
  monitored: DeviceFlag;
  assetInventory: DeviceFlag;
  approved: DeviceFlag;
}

export interface DeviceFlag {
  status: string;
  setBy: string;
  setAt: string;
}

export interface DeviceAttribute {
  name: string;
  value: string;
}

export interface Software {
  packageID: string;
  name: string;
  version: string;
  dateInstalled: string;
  dateLastInventoried: string;
  installationPath: string;
}

export interface Peripheral {
  name: string;
  type: string;
  driverVersion: string;
  driverDate: string;
  driverName: string;
  manufacturer: string;
}

export interface Drive {
  model: string;
  fileSystem: string;
  letter: string;
  volumeName: string;
  size: number;
  freeSpace: number;
  isMissing: boolean;
  isSolidState: boolean;
  isInternal: boolean;
}

export interface DeviceService {
  name: string;
  description: string;
  state: string;
  startup: string;
  serviceType: string;
  username: string;
  pathname: string;
}

export interface MemorySlot {
  slotName: string;
  formFactor: string;
  type: string;
  dataWidth: number;
  size: number;
  speed: number;
  manufacturer: string;
}

export interface VideoCard {
  videoProcessor: string;
  mode: string;
  memory: string;
  architecture: string;
  memoryType: string;
  bitsPerPixel: number;
}

export interface DeviceLastBackup {
  backupId: string;
  backedUpAt: string;
  source: string;
  size: number;
  status: string;
  error: DeviceBackupError;
}

export interface DeviceKey {
  type: string;
  value: string;
  createdAt: string;
  createdBy: string;
  lastSync: string;
  apiConfigId: string;
  tenantId: string;
  apiConnectionId: string;
  apiSiteId: string;
  status: string;
}

export interface DeviceOrganization {
  name: string;
}

export interface DeviceRegion {
  name: string;
}

export interface DeviceBuilding {
  name: string;
}

export interface DeviceControlSystem {
  name: string;
}

export interface DeviceBackupQueryResults {
  items: DeviceBackupRecord[];
  paging: PagingResults;
}

export interface DeviceBackupError {
  reason: string;
  cause: string;
  effect: string;
  file: string;
}

export interface DeviceBackup {
  id: string;
  organizationId: string;
  regionId: string;
  buildingId: string;
  controlSystemId: string;
  deviceId: string;
  backupId: string;
  source: string;
  size: number;
  status: string;
  error: DeviceBackupError;
  isDeleted: boolean;
  keys: ForeignKey[];
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface DeviceBackupDevice {
  id: string;
  displayName: string;
}

export interface DeviceBackupRegion {
  id: string;
  name: string;
}

export interface DeviceBackupBuilding {
  id: string;
  name: string;
}

export interface DeviceBackupControlSystem {
  id: string;
  name: string;
  systemType: number;
}

export type DeviceBackupInput = {
  limit?: number;
  offset?: number;
  name?: string;
  regionId?: string[];
  buildingId?: string[];
  controlSystemId?: string[];
  systemType?: number[];
  isBackedUp?: boolean;
  deviceName?: string;
  controlSystemName?: string;
  backupStatus?: string[];
};

export type DeviceBackupRecord = {
  backup: DeviceBackup;
  device: DeviceBackupDevice;
  controlSystem: DeviceBackupControlSystem;
  region: DeviceBackupRegion;
  building: DeviceBackupBuilding;
};

export interface DeviceLastBackupQueryResults {
  items: DeviceLastBackupRecord[];
  paging: PagingResults;
}

export type DeviceLastBackupRecord = {
  device: Device;
  controlSystem: DeviceLastBackupControlSystem;
  region: DeviceLastBackupRegion;
  building: DeviceLastBackupBuilding;
};

export interface DeviceLastBackupRegion {
  name: string;
}

export interface DeviceLastBackupBuilding {
  name: string;
}

export interface DeviceLastBackupControlSystem {
  name: string;
  systemType: number;
}

export interface DeviceLastBackupFilterOptions {
  region: FilterOption[];
  building: FilterOption[];
  controlSystem: FilterOption[];
  controlSystemType: FilterOption[];
  device: FilterOption[];
  backupStatus: FilterOption[];
}

export interface FilterOption {
  name: string;
  value: string;
}

export type DeviceBackupConnectionInput = {
  from?: string;
  pageSize?: number;
  page?: number;
  sortField?: string;
  sortDirection?: string;
  regionId?: string[];
  buildingId?: string[];
  controlSystemId?: string[];
  deviceId?: string[];
  systemType?: string[];
  deviceName?: string;
  controlSystemName?: string;
  backupStatus?: string[];
  isBackupStale?: string[];
  lastBackupStatus?: string[];
};

export interface DeviceFilterOptions {
  type: FilterStringValue[];
  vendor: FilterStringValue[];
  operatingSystem: FilterStringValue[];
  firmware: FilterStringValue[];
  region: FilterStringValue[];
  building: FilterStringValue[];
  models: FilterStringValue[];
}

export interface FilterStringValue {
  name: string;
  value: string;
}

export interface FilterIntValue {
  value: number;
}
export interface FilterValue {
  text: string;
  value: string;
}

// export interface DeviceQueryResults {
//   devices: Device[];
//   paging: DevicePaging;
// }

// export interface DevicePaging {
//   page: number;
//   pageSize: number;
//   totalRecords: number;
// }

export type DeviceFilters = {
  organizationId?: string[];
  regionId?: string[];
  buildingId?: string[];
  controlSystemId?: string[];
  deviceId?: string[];
  operatingSystem?: string[];
  firmware?: string[];
  type?: string[];
  vendor?: string[];
  model?: string[];
  reference?: string[];
  status?: string[];
  searchTerm?: string;
  isManaged?: string[];
  isMonitored?: string[];
  searchIpAddress?: string;
  noBackup?: string[];
  staleBackups?: string[];
  noAntiVirus?: string[];
  incompleteDeviceType?: string[];
  operatingSystemPastEndOfSupport?: string[];
  operatingSystemApproachingEndOfSupport?: string[];
  lowDiskSpace?: string[];
};

export type DevicesConnectionInput = {
  from?: string;
  pageSize?: number;
  page?: number;
  sortField?: string;
  sortDirection?: string;
  organizationId?: string[];
  regionId?: string[];
  buildingId?: string[];
  controlSystemId?: string[];
  vendor?: string[];
  model?: string[];
  type?: string[];
  isManaged?: string[];
  isMonitored?: string[];
  operatingSystem?: string[];
  firmware?: string[];
  reference?: string[];
  status?: string[];
  searchTerm?: string;
  searchIpAddress?: string;
  noBackup?: string[];
  staleBackups?: string[];
  noAntiVirus?: string[];
  incompleteDeviceType?: string[];
  operatingSystemPastEndOfSupport?: string[];
  operatingSystemApproachingEndOfSupport?: string[];
  lowDiskSpace?: string[];
};

export interface VariableCollection {
  source: string;
  configID: string;
  deviceKey: string;
  variables: Variable[];
  createdAt: string;
}

export interface DeviceVariableMetaData {
  label: string;
  purpose: string;
  location: string;
}

export interface Variable {
  id: string;
  name: string;
  label: string;
  type: string;
  protocol: string;
  isNumeric: boolean;
  scale: string;
  value: number;
  stringValue: string;
  minValue: string;
  maxValue: string;
  lastUpdateTime: string;
  ibdLabel: string;
  purpose: string;
  createdAt: string;
  updatedAt: string;
  metaData: DeviceVariableMetaData;
}

export interface VariableExtended extends Variable {
  source: string;
  configID: string;
  deviceKey: string;
}
